import { UserModalLayout } from '@features/components/UserModalLayout';
import { InfoRow } from '@features/modals/UserInfoModal/components';
import { ModalContentSection } from '@features/modals/UserInfoModal/components/ModalContentSection';
import { ModalHeaderWrapper } from '@features/modals/UserInfoModal/components/ModalHeaderWrapper';
import { PropsWithChildren } from 'react';
import {
  MdOutlineLocalHospital,
  MdOutlineLocationOn,
  MdOutlineMap,
  MdOutlineMoreTime,
  MdOutlineStickyNote2,
} from 'react-icons/md';
import { ConditionalRender, TrialProgramLogos } from '@ui';
import { Spinner } from '@ds';
import { format } from 'date-fns-tz';
import { useTxCenterByIdQuery } from '@/queries/tx-centers';
import { Uuid } from '@/api/types';
import { getLocationAddress } from '@/utils/helpers';
import { useCountriesData } from '@/contexts/CountriesDataProvider';

const size = 28;

export const TxCenterDetailsModal = ({ children, txCenterId }: { txCenterId: Uuid } & PropsWithChildren) => {
  const { data } = useTxCenterByIdQuery(txCenterId);
  const { resolveSubdivisionName } = useCountriesData();
  const { code, name, notes, location, logoPath, trialPrograms, trialProgramNotes } = data ?? {};
  const { country, timezone } = location ?? {};
  const shortTz = format(new Date(), 'zzz', { timeZone: timezone });

  return (
    <UserModalLayout trigger={children}>
      <ConditionalRender show={!!data} placeholder={<Spinner />}>
        <ModalHeaderWrapper
          photo={logoPath}
          header={name!}
          subHeader="TX Center"
          topContent={
            <div className="shrink-0">
              <TrialProgramLogos trialPrograms={trialPrograms} />
            </div>
          }
          avatarProps={{
            type: 'facility',
            fallbackInitials: false,
            name: code || location?.state,
          }}
        />
        <ModalContentSection header="Location Information">
          <div className="grid grid-cols-2 gap-8">
            <InfoRow name="TX Center Code" value={code} icon={<MdOutlineLocalHospital size={size} />} />
            <InfoRow name="Time Zone" value={shortTz} icon={<MdOutlineMoreTime size={size} />} />
            <InfoRow
              name="Address"
              withTooltip
              withTruncate={false}
              value={getLocationAddress(location, resolveSubdivisionName)}
              icon={<MdOutlineLocationOn size={size} />}
            />
            <InfoRow name="Country" value={country} icon={<MdOutlineMap size={size} />} />
          </div>
        </ModalContentSection>
        <ModalContentSection header="Additional  Information">
          <div className="flex flex-col gap-4">
            <InfoRow withTruncate={false} name="Notes" value={notes} icon={<MdOutlineStickyNote2 size={size} />} />
            <ConditionalRender show={!!trialProgramNotes}>
              <InfoRow
                withTruncate={false}
                name="Trial Notes"
                value={trialProgramNotes}
                icon={<MdOutlineStickyNote2 size={size} />}
              />
            </ConditionalRender>
          </div>
        </ModalContentSection>
      </ConditionalRender>
    </UserModalLayout>
  );
};
